// ============================================================================
// Fiscal adapters — per-country invoicing rules behind ONE interface.
// ----------------------------------------------------------------------------
// The app was built Spain-first: IVA/IRPF/IBAE tax math (lib/taxes.js) and the
// Verifactu / TicketBAI / Facturae artifacts are all AEAT-shaped. To serve more
// countries we route every country-specific decision in the INVOICING flow
// through a fiscal adapter, chosen by the account owner's `profiles.country_code`
// (see backend/country_localization.sql).
//
// The materials ERP is country-neutral and does NOT go through here — only
// invoices / estimates / tickets do.
//
// Adapter contract (see ./es.js for the reference implementation):
//   countryCode                         'ES' | 'PK'
//   meta       { name, currency, currencySymbol, locale,
//                taxIdLabel, clientTaxIdLabel }
//   taxFeatures{ recargoEquiv, ibae, irpf }   // which tax controls the editor shows
//   computeTotals(lines, opts) -> {           // normalized totals for UI + template
//                byRate, subtotal, taxTotal, taxLabel,
//                extraTotals:[{key,label,amount}],
//                deductions:[{key,label,amount}], grandTotal }
//   requiredClientFields()      -> string[]   // e.g. ['tax_id'] | ['tax_id','hs_code']
//   validateTaxId(id)           -> boolean
//   einvoice   { enabled, featureKey, label,
//                submit({invoice,lines,profile,client}) -> Promise<{fiscalNumber,qr,status,raw}> }
//
// Phase 0 only defines the adapters + this registry. The invoicing pages are
// wired to them in Phase 1; until then the Spanish path is unchanged.
// ============================================================================
import { supabase } from '../supabase';
import { getOwnerId } from '../tenant';
import { esAdapter } from './es';
import { pkAdapter } from './pk';

export const DEFAULT_COUNTRY = 'ES';

// Registry. Add a country by dropping in ./xx.js and registering it here.
const ADAPTERS = {
  ES: esAdapter,
  PK: pkAdapter,
};

// Countries the UI may offer (admin country selector, onboarding). Mirrors the
// registry keys; label is shown in pickers.
export const SUPPORTED_COUNTRIES = [
  { code: 'ES', name: 'España',   currency: 'EUR', taxAuthority: 'AEAT (Verifactu)' },
  { code: 'PK', name: 'Pakistan', currency: 'PKR', taxAuthority: 'FBR (Digital Invoicing)' },
];

// Resolve an adapter from a profile object or a bare country code. Always
// returns a valid adapter (falls back to the default country) so callers never
// have to null-check.
export function getAdapter(profileOrCode) {
  const code = typeof profileOrCode === 'string'
    ? profileOrCode
    : (profileOrCode?.country_code || DEFAULT_COUNTRY);
  return ADAPTERS[String(code || DEFAULT_COUNTRY).toUpperCase()] || ADAPTERS[DEFAULT_COUNTRY];
}

// --- Owner country-code cache (mirrors the tenant.js pattern) --------------
// The active fiscal country is a property of the account OWNER, so team members
// inherit it. Cached per owner so the invoicing pages don't refetch on mount.
let _cache = null; // { ownerId, countryCode }

// Last country resolved on this browser, persisted so country-driven chrome
// (e.g. the Layout language switcher) can render right on first paint instead
// of flashing the ES default while the profile fetch is in flight.
const LAST_COUNTRY_KEY = 'zbilling_last_country';

export function getCachedCountryCode() {
  try {
    const code = localStorage.getItem(LAST_COUNTRY_KEY);
    return code && ADAPTERS[code] ? code : null;
  } catch { return null; }
}

export function clearCountryCache() {
  _cache = null;
}

// Fetch the current account owner's country_code (defaults to 'ES'). Fails safe
// to the default so a missing column / policy never breaks invoicing.
export async function fetchCountryCode() {
  const ownerId = await getOwnerId();
  if (!ownerId) return DEFAULT_COUNTRY;
  if (_cache && _cache.ownerId === ownerId) return _cache.countryCode;
  const { data, error } = await supabase
    .from('profiles')
    .select('country_code')
    .eq('id', ownerId)
    .single();
  if (error) {
    console.warn('fetchCountryCode failed:', error.message);
    return DEFAULT_COUNTRY;
  }
  const countryCode = data?.country_code || DEFAULT_COUNTRY;
  _cache = { ownerId, countryCode };
  try { localStorage.setItem(LAST_COUNTRY_KEY, countryCode); } catch { /* ignore */ }
  return countryCode;
}
